import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import styled from 'styled-components';
import { useViewpoint, VIEWPOINTS } from '../../contexts/ViewpointContext';
import { HERO_SPACING } from '../../theme/spacing';
import { RainbowButton } from '../ui/rainbow-button';

// Add CSS variables for language toggle
const styleVariables = `
  :root {
    /* Language toggle controls */
    --lang-toggle-bg: rgba(255, 255, 255, 1);
    --lang-toggle-text: rgba(0, 0, 0, 0.9);
    --lang-toggle-hover-bg: rgba(245, 245, 245, 1);
    --lang-toggle-hover-text: rgba(0, 0, 0, 1);
    --lang-toggle-shadow-x: 0px;
    --lang-toggle-shadow-y: 2px;
    --lang-toggle-shadow-blur: 4px;
    --lang-toggle-shadow-spread: 0px;
    --lang-toggle-shadow-color: rgba(0, 0, 0, 0.1);
    --lang-toggle-border-color: rgba(0, 0, 0, 0.1);
  }
`;

// Insert styles into document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = styleVariables;
  document.head.appendChild(style);
}

const HeroContainer = styled.div`
  position: relative;
  min-height: ${props => {
    if (props.$viewpoint === VIEWPOINTS.MOBILE) return 'calc(100dvh - 95px)';
    return '100dvh'
  }};

  @media (max-width: 640px) {
    min-height: calc(100dvh - 95px);
  }
`;

const TopBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
`;

const ContentWrapper = styled.div`
  position: relative;
  padding: ${props => {
    if (props.$viewpoint === VIEWPOINTS.MOBILE) {
      return '140px 24px 0'; // Increased top padding on mobile
    }
    return `${HERO_SPACING[props.$viewpoint].contentTopPadding} 24px 0`;
  }};
  margin: 0 auto;
  width: 100%;
  max-width: 1400px;

  @media (min-width: 640px) {
    padding-left: 40px;
    padding-right: 40px;
  }

  @media (min-width: 1024px) {
    padding-left: 80px;
    padding-right: 80px;
  }

  /* Content container for title and subtitle */
  > div {
    max-width: ${props => props.$viewpoint === VIEWPOINTS.MOBILE ? '100%' : '1200px'};
    margin: 0;
    
    @media (min-width: 1024px) {
      margin: 0 auto;
    }
  }
`;

const HeroTitle = styled.h1`
  font-size: ${props => {
    if (props.$viewpoint === VIEWPOINTS.MOBILE) return '48px';
    if (props.$viewpoint === VIEWPOINTS.NARROW) return '72px';
    if (props.$viewpoint === VIEWPOINTS.WIDE) return props.theme.typography.hero.title.fontSize;
    return 'clamp(4rem, 8vw, 120px)';
  }};
  line-height: ${props => props.theme.typography.hero.title.lineHeight};
  font-weight: ${props => props.theme.typography.hero.title.fontWeight};
  letter-spacing: ${props => props.theme.typography.hero.title.letterSpacing};
  text-shadow: ${props => props.theme.typography.hero.title.textShadow};
  margin-bottom: ${props => HERO_SPACING[props.$viewpoint].titleToSeparator};
  color: ${props => props.theme.colors.text.primary};
  max-width: ${props => props.$language === 'fr' ? '1100px' : '1000px'};
  
  @media (max-width: 480px) {
    padding-right: 0;
  }
`;

const Separator = styled.div`
  width: ${props => props.theme.typography.hero.separator.width};
  height: ${props => props.theme.typography.hero.separator.height};
  margin-top: ${props => HERO_SPACING[props.$viewpoint].separatorTopMargin};
  margin-bottom: ${props => HERO_SPACING[props.$viewpoint].separatorBottomMargin};
  background-color: ${props => props.theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.hero.subtitle.fontSize};
  line-height: ${props => props.theme.typography.hero.subtitle.lineHeight};
  font-weight: ${props => props.theme.typography.hero.subtitle.fontWeight};
  margin-bottom: ${props => props.theme.typography.hero.subtitle.marginBottom};
  text-shadow: ${props => props.theme.typography.hero.subtitle.textShadow};
  color: ${props => props.theme.colors.text.primary};
`;

const ButtonContainer = styled.div`
  margin-top: 32px;
  display: ${props => props.$viewpoint === VIEWPOINTS.MOBILE ? 'none' : 'block'};
`;

const WaitlistBanner = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  background-color: ${props => props.theme.colors.background.overlay};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  padding: 8px 8px 8px 20px;
  margin-bottom: 28px;
  text-decoration: none;
  backdrop-filter: blur(6px);
  transition: border-color 0.25s ease, background-color 0.25s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.28);
  }

  @media (max-width: 480px) {
    gap: 10px;
    padding: 6px 6px 6px 16px;
  }
`;

const WaitlistBannerText = styled.span`
  color: ${props => props.theme.colors.text.secondary};
  font-size: 14px;
  letter-spacing: 0.01em;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 12px;
    white-space: normal;
  }
`;

const WaitlistBannerCta = styled.span`
  color: ${props => props.theme.colors.text.primary};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 6px 16px;

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 5px 12px;
  }
`;

const Hero = () => {
  const { theme } = useTheme();
  const { t, tSafe, language, setLanguage } = useLanguage();
  const { currentViewpoint } = useViewpoint();

  const handleGetStarted = () => {
    const digitalWorkersSection = document.getElementById('digitalWorkers');
    if (digitalWorkersSection) {
      digitalWorkersSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleWaitlistClick = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact-us');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Safe title handling for split operation
  const heroTitle = t('hero.title');
  const titleLines = typeof heroTitle === 'string' ? heroTitle.split('\n') : [heroTitle];

  return (
    <HeroContainer $viewpoint={currentViewpoint}>
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://cdn.nexastaff.ai/back.jpg)'
        }}
      />
      
      {/* Logo and Language Toggle */}
      <TopBar>
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 pt-4">
          <div className="flex-shrink-0" style={{ marginLeft: '0px', marginTop: '10px' }}>
            <img 
              src="https://cdn.nexastaff.ai/logo.png?v=4" 
              alt="NEXA" 
              className="h-12 w-auto object-contain" 
              style={{ display: 'block' }}
            />
          </div>
          
          <div className="flex items-center gap-2" style={{ marginRight: '0px', marginTop: '10px' }}>
            <a
              href="https://hq.nexastaff.ai/"
              className="rounded-lg px-4 py-1.5 text-sm font-medium transition-all"
              style={{
                backgroundColor: 'var(--lang-toggle-bg)',
                color: 'var(--lang-toggle-text)',
                boxShadow: 'var(--lang-toggle-shadow-x) var(--lang-toggle-shadow-y) var(--lang-toggle-shadow-blur) var(--lang-toggle-shadow-spread) var(--lang-toggle-shadow-color)',
                border: '1px solid var(--lang-toggle-border-color)'
              }}
            >
              Login
            </a>
            <button 
              onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
              className="rounded-lg px-4 py-1.5 text-sm font-medium transition-all"
              style={{
                minWidth: language === 'fr' ? '3.5rem' : '3rem',
                fontSize: language === 'fr' ? '13px' : '14px',
                backgroundColor: 'var(--lang-toggle-bg)',
                color: 'var(--lang-toggle-text)',
                boxShadow: 'var(--lang-toggle-shadow-x) var(--lang-toggle-shadow-y) var(--lang-toggle-shadow-blur) var(--lang-toggle-shadow-spread) var(--lang-toggle-shadow-color)',
                border: '1px solid var(--lang-toggle-border-color)'
              }}
            >
              {language === 'en' ? 'FR' : 'EN'}
            </button>
          </div>
        </div>
      </TopBar>
      
      {/* Content */}
      <ContentWrapper theme={theme} $viewpoint={currentViewpoint}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-5xl">
            <WaitlistBanner theme={theme} href="#contact-us" onClick={handleWaitlistClick}>
              <WaitlistBannerText theme={theme}>{t('hero.banner')}</WaitlistBannerText>
              <WaitlistBannerCta theme={theme}>{t('hero.bannerCta')}</WaitlistBannerCta>
            </WaitlistBanner>
            <HeroTitle theme={theme} $language={language} $viewpoint={currentViewpoint}>
              {titleLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </HeroTitle>
            <Separator theme={theme} $viewpoint={currentViewpoint} />
            <Subtitle theme={theme}>
              {t('hero.subtitle')}
            </Subtitle>
            <ButtonContainer $viewpoint={currentViewpoint}>
              <RainbowButton onClick={handleGetStarted}>
                {t('hero.cta')} →
              </RainbowButton>
            </ButtonContainer>
          </div>
        </div>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default Hero; 